/**
 * Speed Round: a per-question time limit (drill settings `time_limit`).
 * Derived from the try timer, so it pauses and restarts wherever the timer
 * does (answer check, help, dialogs, the next question). The time spent
 * speaking the question is not counted. Reaching 0 calls `onTimeout` once.
 */
import { computed, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import { formatTime } from 'shared/composables/useTimer/useTimer';
import type { DrillTimeLimitSettings } from 'src/types/course/drill';

/** The clock turns red under this many ms. */
export const TIME_WARNING_MS = 3000;

export interface UseDrillTimeLimitOptions {
  timeLimit: MaybeRefOrGetter<DrillTimeLimitSettings | null | undefined>;
  /** The try timer (ms, counts up). */
  elapsedMs: Readonly<Ref<number>>;
  questionAudioMs: Readonly<Ref<number>>;
  isSpeakingQuestion: Readonly<Ref<boolean>>;
  /** false while an answer is checked, the step is done or the view is gone. */
  canTimeout: () => boolean;
  onTimeout: () => unknown;
  /** The count-up clock text shown when there is no visible countdown. */
  formattedTime: Readonly<Ref<string>>;
}

export function useDrillTimeLimit(opts: UseDrillTimeLimitOptions) {
  const limitMs = computed(() => {
    const limit = toValue(opts.timeLimit);
    if (!limit?.is_time_limited || !limit.seconds || limit.seconds <= 0) {
      return null;
    }
    return limit.seconds * 1000;
  });

  const isTimeLimited = computed(() => limitMs.value !== null);

  /** ms left for the current try (null without a limit). */
  const remainingMs = computed(() => {
    if (limitMs.value === null) return null;
    if (opts.isSpeakingQuestion.value) return limitMs.value;
    const left =
      limitMs.value + opts.questionAudioMs.value - opts.elapsedMs.value;
    return Math.min(limitMs.value, Math.max(0, left));
  });

  watch(
    () => remainingMs.value === 0,
    (isOut) => {
      if (isOut && opts.canTimeout()) opts.onTimeout();
    },
  );

  const showClock = computed(
    () => isTimeLimited.value && toValue(opts.timeLimit)?.show_clock !== false,
  );

  /** StepHeader's clock props: the countdown, or the usual count-up time. */
  const clockProps = computed(() => {
    if (!showClock.value || remainingMs.value === null) {
      return { formattedTime: opts.formattedTime.value, timeWarning: false };
    }
    return {
      formattedTime: formatTime(Math.ceil(remainingMs.value / 1000) * 1000),
      timeWarning: remainingMs.value <= TIME_WARNING_MS,
    };
  });

  return { isTimeLimited, remainingMs, clockProps };
}

export type UseDrillTimeLimitReturn = ReturnType<typeof useDrillTimeLimit>;
